(() => {
  if (window.__rtoCartUnifyBound) return;
  window.__rtoCartUnifyBound = true;

  const root =
    window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';

  const fetchCart = async () => {
    const r = await fetch(root + 'cart.js', { credentials: 'same-origin' });
    if (!r.ok) throw new Error('Unable to fetch cart.');
    return await r.json();
  };

  const postJSON = async (url, payload) => {
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!r.ok) {
      let text = '';
      try { text = await r.text(); } catch (e) {}
      throw new Error(text || `Request failed: ${r.status}`);
    }

    const contentType = r.headers.get('content-type') || '';
    if (contentType.includes('application/json')) return await r.json();
    return await r.text();
  };

  const parseNumber = (v, fallback = NaN) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const normSched = (v) => {
    const s = String(v || '').toLowerCase().replace(/[-\s]/g, '');
    if (s === 'biweekly' || s === 'biweek' || s === 'biweeklypayment') return 'biweekly';
    if (s === 'semimonthly' || s === 'semimonth' || s === 'twicepermonth') return 'semimonthly';
    if (s === 'monthly' || s === 'month') return 'monthly';
    return 'weekly';
  };

  const normPlan = (v) => {
    const s = String(v == null ? '' : v).trim();
    const m = s.match(/(12|15|18|24)/);
    if (m) return Number(m[1]);

    const n = parseInt(s, 10);
    if ([12, 15, 18, 24].includes(n)) return n;

    return 12;
  };

  const paymentsCount = (months, sched) => {
    const m = Number(months) || 0;
    if (sched === 'monthly') return Math.max(1, m);
    if (sched === 'semimonthly') return Math.max(1, m * 2);
    if (sched === 'biweekly') return Math.max(1, Math.round((m * 26) / 12));
    return Math.max(1, Math.round((m * 52) / 12));
  };

  const readJSONConfig = (wrap) => {
    const el = wrap.querySelector('[data-rto-cart-config]');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('Invalid RTO cart config JSON.', e);
      return null;
    }
  };

  const getWrapConfig = (wrap) => {
    const jsonConfig = readJSONConfig(wrap) || {};

    const config = {
      cashMultiplier: parseNumber(
        jsonConfig.cashMultiplier,
        parseNumber(wrap?.dataset?.cashMultiplier, NaN)
      ),
      epoMultiplier: parseNumber(
        jsonConfig.epoMultiplier,
        parseNumber(wrap?.dataset?.epoMultiplier, NaN)
      ),
      multipliers: {
        12: parseNumber(
          jsonConfig?.multipliers?.['12'],
          parseNumber(wrap?.dataset?.multiplier12, NaN)
        ),
        15: parseNumber(
          jsonConfig?.multipliers?.['15'],
          parseNumber(wrap?.dataset?.multiplier15, NaN)
        ),
        18: parseNumber(
          jsonConfig?.multipliers?.['18'],
          parseNumber(wrap?.dataset?.multiplier18, NaN)
        ),
        24: parseNumber(
          jsonConfig?.multipliers?.['24'],
          parseNumber(wrap?.dataset?.multiplier24, NaN)
        ),
      },
    };

    return config;
  };

  const getLineMultiplier = (props, wrapConfig, planMonths) => {
    const fromWrap = wrapConfig?.multipliers?.[planMonths];
    if (Number.isFinite(fromWrap) && fromWrap > 0) return fromWrap;

    const rawStoredMultiplier = parseNumber(props._rto_multiplier, NaN);
    const rawStoredPlan = normPlan(
      props._rto_plan_months || props['Ownership Plan'] || planMonths
    );

    if (
      Number.isFinite(rawStoredMultiplier) &&
      rawStoredMultiplier > 0 &&
      rawStoredPlan === planMonths
    ) {
      return rawStoredMultiplier;
    }

    return NaN;
  };

  const buildVisibleSchedule = (sched) => {
    if (sched === 'biweekly') return 'Bi-Weekly';
    if (sched === 'semimonthly') return 'Semi-Monthly';
    if (sched === 'monthly') return 'Monthly';
    return 'Weekly';
  };

  const buildVisiblePlan = (planMonths) => `${planMonths} Month`;

  const calc = (baseCents, sched, planMonths, props, wrapConfig) => {
    const base = Math.max(0, parseInt(baseCents, 10) || 0);
    const cashMultiplier = wrapConfig.cashMultiplier;
    const epoMultiplier = wrapConfig.epoMultiplier;
    const multiplier = getLineMultiplier(props, wrapConfig, planMonths);

    if (!(Number.isFinite(multiplier) && multiplier > 0)) {
      throw new Error(
        `Missing multiplier for ${planMonths}-month plan. ` +
        `Received 12=${wrapConfig?.multipliers?.[12]}, ` +
        `15=${wrapConfig?.multipliers?.[15]}, ` +
        `18=${wrapConfig?.multipliers?.[18]}, ` +
        `24=${wrapConfig?.multipliers?.[24]}.`
      );
    }

    if (!(Number.isFinite(cashMultiplier) && cashMultiplier > 0)) {
      throw new Error(`Missing cash multiplier. Received ${cashMultiplier}.`);
    }

    const total = Math.round(base * multiplier);
    const cash = Math.round(base * cashMultiplier);
    const lease = total - cash;
    const epo = Math.round(total * Math.max(0, epoMultiplier));
    const pay = Math.round(total / paymentsCount(planMonths, sched));

    return { cash, total, lease, pay, epo, multiplier };
  };

  const setCheckoutDisabled = (disabled) => {
    const selectors = [
      '#CartDrawer-Checkout',
      'button.cart__checkout',
      '#cart-notification-form button[name="checkout"]',
    ];

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((btn) => {
        btn.disabled = !!disabled;
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    });
  };

  const syncCheckoutDisabled = () => {
    const mismatch = !!document.querySelector('[data-rto-unify][data-mismatch="1"]');
    setCheckoutDisabled(mismatch);
  };

  const setActive = (wrap, selector, value) => {
    wrap.querySelectorAll(selector).forEach((btn) => {
      const on = String(btn.getAttribute('data-value')) === String(value);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  const getSelection = (wrap) => {
    let schedule = wrap.dataset.selectedSchedule || '';
    let plan = wrap.dataset.selectedPlan || '';

    if (!schedule) schedule = wrap.dataset.currentSchedule || 'weekly';
    if (!plan) plan = wrap.dataset.currentPlan || '12';

    return {
      schedule: normSched(schedule),
      planMonths: normPlan(plan),
    };
  };

  const applyToAll = async (wrap, applyBtn) => {
    const { schedule, planMonths } = getSelection(wrap);
    const wrapConfig = getWrapConfig(wrap);

    applyBtn.disabled = true;
    applyBtn.setAttribute('aria-busy', 'true');

    try {
      while (true) {
        const cart = await fetchCart();

        const item = cart.items.find((it) => {
          const props = it.properties || {};
          const curSched = normSched(
            props._rto_schedule ||
              props['Payment Schedule'] ||
              wrap.dataset.currentSchedule ||
              'weekly'
          );
          const curPlan = normPlan(
            props._rto_plan_months ||
              props['Ownership Plan'] ||
              wrap.dataset.currentPlan ||
              '12'
          );
          return curSched !== schedule || curPlan !== planMonths;
        });

        if (!item) break;

        const props = Object.assign({}, item.properties || {});
        const baseCents =
          parseInt(props._rto_base_price_cents, 10) || item.final_price || item.price || 0;

        const out = calc(baseCents, schedule, planMonths, props, wrapConfig);

        props._rto_schedule = schedule;
        props._rto_plan_months = String(planMonths);
        props._rto_multiplier = String(out.multiplier);
        props._rto_base_price_cents = String(baseCents);
        props._rto_cash_cents = String(out.cash);
        props._rto_total_cents = String(out.total);
        props._rto_lease_cents = String(out.lease);
        props._rto_payment_cents = String(out.pay);
        props._rto_epo_cents = String(out.epo);
        props['Payment Schedule'] = buildVisibleSchedule(schedule);
        props['Ownership Plan'] = buildVisiblePlan(planMonths);

        await postJSON(root + 'cart/change.js', {
          id: item.key,
          quantity: item.quantity,
          properties: props,
        });
      }

      window.location.reload();
    } catch (err) {
      console.error('RTO cart unify failed:', err);
      alert(err && err.message ? err.message : 'Unable to update cart items.');
    } finally {
      applyBtn.disabled = false;
      applyBtn.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', async (e) => {
    const wrap = e.target.closest('[data-rto-unify]');
    if (!wrap) return;

    const schedBtn = e.target.closest('[data-rto-schedule-option]');
    if (schedBtn) {
      const v = schedBtn.getAttribute('data-value') || 'weekly';
      wrap.dataset.selectedSchedule = v;
      setActive(wrap, '[data-rto-schedule-option]', v);
      syncCheckoutDisabled();
      return;
    }

    const planBtn = e.target.closest('[data-rto-plan-option]');
    if (planBtn) {
      const v = planBtn.getAttribute('data-value') || '12';
      wrap.dataset.selectedPlan = v;
      setActive(wrap, '[data-rto-plan-option]', v);
      syncCheckoutDisabled();
      return;
    }

    const applyBtn = e.target.closest('[data-rto-apply]');
    if (applyBtn) {
      await applyToAll(wrap, applyBtn);
    }
  });

  syncCheckoutDisabled();

  // Perf: debounce so the checkout-state check runs at most every ~120ms
  // instead of on every single DOM mutation.
  let rtoSyncTimer = null;
  const debouncedSync = () => {
    clearTimeout(rtoSyncTimer);
    rtoSyncTimer = setTimeout(syncCheckoutDisabled, 120);
  };
  new MutationObserver(debouncedSync).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();